/**
 * Holds the page still while a modal dialog is open (ADR 0191), in place of
 * Radix's scroll lock. Radix's adds an inline <style> element, which the
 * Content Security Policy's `style-src 'self'` refuses, so the page behind a
 * dialog kept scrolling. Styles set from script, through the CSSOM, are
 * allowed: this sets them on <html> and <body> directly.
 *
 * - **The page's overflow is hidden,** so neither the wheel, the keys nor
 *   its scroll bar scroll it. Where it was scrolled to stays as it was.
 * - **The scroll bar's width is kept** as padding on <body>, so nothing
 *   behind moves sideways as the bar goes. Where scroll bars sit over the
 *   page (phones, macOS) there's none to keep.
 * - **Counted:** Owner tools over a block's popup holds the page until both
 *   have closed. Each release counts once.
 */

/** How many dialogs hold the page now, and how to put it back. */
let holds = 0;
let putBack: (() => void) | null = null;

function hold(): () => void {
  const root = document.documentElement;
  const body = document.body;
  // Measured before the overflow is hidden, which takes the bar away.
  const scrollBar = root.clientWidth > 0 ? Math.max(0, window.innerWidth - root.clientWidth) : 0;
  const before = { overflow: root.style.overflow, paddingRight: body.style.paddingRight };
  if (scrollBar > 0) {
    const padding = Number.parseFloat(getComputedStyle(body).paddingRight) || 0;
    body.style.paddingRight = `${String(padding + scrollBar)}px`;
  }
  root.style.overflow = "hidden";
  return () => {
    root.style.overflow = before.overflow;
    body.style.paddingRight = before.paddingRight;
  };
}

/** Holds the page still until the returned release is called. */
export function lockPageScroll(): () => void {
  if (holds === 0) {
    putBack = hold();
  }
  holds += 1;
  let released = false;
  return () => {
    if (released) {
      return;
    }
    released = true;
    holds -= 1;
    if (holds === 0) {
      putBack?.();
      putBack = null;
    }
  };
}
